import Bugsnag from "@bugsnag/js";
import app from "@/app";
const moment = window.moment;

// eslint-disable-next-line no-undef
window.config = CONFIG;

Bugsnag.start({
  apiKey: window.config.bugsnagApiKey,
  releaseStage: window.config.environment,
  appVersion: window.config.version,
});

export default {
  bugsnag: Bugsnag,

  intercom: {
    isMasterUser: false,
    show: true,
    data: null,

    // Booting
    boot: function () {
      if (window.Intercom !== undefined) {
        window.Intercom("boot", {
          app_id: window.config.intercomId,
          hide_default_launcher: true,
        });
      }
    },

    shutdown: function () {
      if (window.Intercom !== undefined) {
        window.Intercom("shutdown");
      }
    },

    // Update Intercom
    update: function (data) {
      data = data || this.data || null;
      this.data = data;
      if (window.Intercom !== undefined) {
        window.Intercom("update", this.data);
      }
    },

    // Sends an Intercom message
    message: function (message) {
      if (window.Intercom !== undefined) {
        window.Intercom("showNewMessage", message);
      }
    },

    event: function (name, data, includeMasterUsers) {
      if (!window.Intercom) return;
      if (!includeMasterUsers && this.isMasterUser) return false;

      window.Intercom("trackEvent", name, data);
      setTimeout(function () {
        window.Intercom("update");
      }, 1000);
    },
  },

  amplitude: {
    isMasterUser: false,

    boot: function () {
      if (!window.amplitude) return;

      window.amplitude.getInstance().init(window.config.amplitudeId);
    },

    identify: function (account, properties) {
      if (!window.amplitude) return;

      window.amplitude.getInstance().setUserId(account);
      window.amplitude.getInstance().setUserProperties(properties);

      if (this.isMasterUser) {
        window.amplitude.getInstance().logEvent("MASTERUSER_ACCESS", { master_user: app.session.attributes.user });
      }
    },

    event: function (name, eventProperties, includeMasterUsers) {
      if (!window.amplitude) return;
      if (!includeMasterUsers && this.isMasterUser) return;

      if (this.isMasterUser) {
        window.amplitude.getInstance().logEvent(name, { master_user: app.session.attributes.user, ...eventProperties });
      } else {
        window.amplitude
          .getInstance()
          .logEvent(name, { account_user: app.session.attributes.user, ...eventProperties });
      }
    },
  },

  gtm: {
    event: function (data) {
      if (window.dataLayer !== undefined && window.dataLayer.push !== undefined) {
        window.dataLayer.push(data);
      }
    },
  },

  zendesk: {
    initializing: false,
    ready: false,
    _cachedToken: null,
    _pending: null,
    _pendingTimeout: null,
    _PREFILL_TIMEOUT_MS: 15000,
    _delegateAttached: false,

    _fetchJwt: function () {
      return fetch(window.config.api + "zendesk/jwt", {
        headers: {
          "X-Auth-Token": app.session.attributes.token,
          "x-account": app.session.attributes.account,
        },
      }).then(function (res) {
        if (!res.ok) {
          throw new Error("Zendesk JWT fetch failed: " + res.status);
        }
        return res.json();
      });
    },

    _buildMetadata: function () {
      var country = "";
      try {
        country = app.session.attributes.details.account.country || "";
      } catch (e) {
        /* noop */
      }
      return { source: "perfit", country: country };
    },

    init: function (lang) {
      if (this.initializing || this.ready || !window.config.zendeskWidgetKeys) return;
      this.initializing = true;
      var self = this;
      var keys = window.config.zendeskWidgetKeys;

      this._fetchJwt()
        .then(function (data) {
          var token = data.data.token;
          var segment = data.data.customer_segment;
          var isVip = segment === "VIP";

          var langKeys = keys[lang] || keys["es"];
          var key = isVip ? langKeys.vip : langKeys["default"];
          if (!key) {
            self.initializing = false;
            return;
          }

          self._cachedToken = token;

          var script = document.createElement("script");
          script.id = "ze-snippet";
          script.src = "https://static.zdassets.com/ekr/snippet.js?key=" + key;
          script.onload = function () {
            self.initializing = false;
            self.ready = true;
            window.zE("messenger", "close");
            // window.zE("messenger", "hide");
            window.zE("messenger:on", "close", function () {
              // window.zE("messenger", "hide");
            });
            self._flushPending();
            window.zE("messenger", "loginUser", function (callback) {
              if (self._cachedToken) {
                var t = self._cachedToken;
                self._cachedToken = null;
                callback(t);
                return;
              }
              self._fetchJwt()
                .then(function (d) {
                  callback(d.data.token);
                })
                .catch(function (err) {
                  console.error("[Zendesk] loginUser error:", err);
                  app.integrations.bugsnag.notify(err);
                  callback(null);
                });
            });
          };
          script.onerror = function () {
            self.initializing = false;
            app.integrations.bugsnag.notify(
              new Error("Failed to load Zendesk Messaging script")
            );
          };
          document.body.appendChild(script);
        })
        .catch(function (err) {
          self.initializing = false;
          console.error("[Zendesk] init fetch error:", err);
          app.integrations.bugsnag.notify(err);
        });
    },

    open: function () {
      if (!this.ready) return;
      window.zE("messenger", "show");
      window.zE("messenger", "open");
    },

    // Abre el chat de Zendesk con un mensaje inicial pre-cargado.
    // Si el widget aún no está listo, guarda el último pending (último gana)
    // y lo despacha al flush; si tras _PREFILL_TIMEOUT_MS no está listo, avisa al usuario.
    openWithPrefill: function (message) {
      var text = typeof message === "string" ? message : String(message || "");
      return this._queueOrDispatch({ action: "newConversation", message: text });
    },

    // Inicia una nueva conversación sin mensaje pre-cargado, adjuntando metadata.
    // Mismo contrato de pending/timeout que openWithPrefill.
    startConversation: function () {
      return this._queueOrDispatch({ action: "newConversation", message: null });
    },

    // Muestra y abre el messenger sobre la conversación existente, sin iniciar
    // una nueva. Mismo contrato de pending/timeout que openWithPrefill.
    openMessenger: function () {
      return this._queueOrDispatch({ action: "open" });
    },

    _queueOrDispatch: function (intent) {
      if (this.ready) {
        return this._dispatch(intent);
      }
      this._pending = intent;
      if (!this._pendingTimeout) {
        var self = this;
        this._pendingTimeout = setTimeout(function () {
          self._onPendingTimeout();
        }, this._PREFILL_TIMEOUT_MS);
      }
      if (!this.initializing && !this.ready && window.config.zendeskWidgetKeys) {
        this.init(app.lang);
      }
      return true;
    },

    _dispatch: function (intent) {
      if (intent && intent.action === "open") {
        return this._openMessenger();
      }
      return this._dispatchNewConversation(intent ? intent.message : null);
    },

    // Muestra y abre el messenger sin iniciar una nueva conversación.
    // Variante de open() sin el guard de `ready` (el dispatch garantiza ready).
    _openMessenger: function () {
      try {
        window.zE("messenger", "show");
        window.zE("messenger", "open");
      } catch (err) {
        console.error("[Zendesk] openMessenger error:", err);
        app.integrations.bugsnag.notify(err);
        return false;
      }
      return true;
    },

    _dispatchNewConversation: function (text) {
      var opts = { metadata: this._buildMetadata() };
      if (text) {
        opts.message = { content: { type: "text", text: text } };
      }
      try {
        window.zE("messenger:ui", "newConversation", opts);
      } catch (err) {
        console.error("[Zendesk] newConversation error:", err);
        app.integrations.bugsnag.notify(err);
        return false;
      }
      window.zE("messenger", "show");
      window.zE("messenger", "open");
      return true;
    },

    _flushPending: function () {
      if (this._pendingTimeout) {
        clearTimeout(this._pendingTimeout);
        this._pendingTimeout = null;
      }
      if (this._pending) {
        var intent = this._pending;
        this._pending = null;
        this._dispatch(intent);
      }
    },

    _onPendingTimeout: function () {
      this._pending = null;
      this._pendingTimeout = null;
      app.integrations.bugsnag.notify(
        new Error("Zendesk newConversation timeout: widget not ready")
      );
      try {
        window.dispatchEvent(new CustomEvent("support-chat:unavailable"));
      } catch (e) {
        /* noop */
      }
    },

    close: function () {
      if (!this.ready) return;
      window.zE("messenger", "close");
    },

    // Delegación global para enlaces renderizados desde JSON/HBS:
    //   <a data-action="support-prefill" data-prefill-message="...">...</a>
    // Se usa para reemplazar los viejos href="javascript:..." (CSP friendly).
    _attachClickDelegate: function () {
      if (this._delegateAttached) return;
      this._delegateAttached = true;
      var self = this;
      document.addEventListener("click", function (e) {
        var target = e.target;
        if (!target || typeof target.closest !== "function") return;
        var el = target.closest('[data-action="support-prefill"]');
        if (!el) return;
        e.preventDefault();
        var message = el.getAttribute("data-prefill-message") || "";
        self.openWithPrefill(message);
      });
    },
  },

  // Starts integrations
  start: function () {
    var session = app.session.toJSON(),
      account = session.account,
      details = session.details || {},
      mainAccount = session.mainAccount,
      companyDetails;

    // Set account name or 'masterusers'
    if (session.userType === "MASTER" || session.isMasterUser) {
      this.intercom.isMasterUser = true;
      mainAccount = "masterusers";
      companyDetails = {
        id: mainAccount,
        name: "Master Users",
      };
    } else {
      // If current account is the mainAccount, send account details to Intercom
      if (account === mainAccount) {
        companyDetails = this.getCompanyDetails();
      } else {
        companyDetails = {
          id: account,
          parent: mainAccount,
        };
      }
    }

    // Identify user in Bugsnag
    Bugsnag.setUser(
      session.isMasterUser ? session.user + "@masterusers" : account,
      details.user.email,
      details.user.name,
    );
    // Delete auth token from session object for security reasons, before including in bugsnag report
    delete session.token;
    // Attach session info to Bugsnag reports
    Bugsnag.addMetadata("session", session);

    // Identify user in Intecom
    this.intercom.update({
      user_id: session.user + "@" + mainAccount,
      name: details.user.name,
      email: details.user.email,
      created_at: moment(details.user.created).unix(),
      role: details.user.role,
      company: companyDetails,
    });

    // Init Zendesk Messaging Widget + attach delegate for data-action="support-prefill"
    this.zendesk._attachClickDelegate();
    this.zendesk.init(app.lang);

    // Boot Amplitude
    if (session.userType !== "MASTER") {
      this.amplitude.isMasterUser = false;
      this.amplitude.identify(account, {
        plan: details.plan.type,
        plan_state: details.plan.state,
        plan_subscription_usd: details.plan.subscription === undefined ? 0 : details.plan.subscription.price.totalUSD,
        is_agency: details.account.container,
        //children: details.stats.children,
        industry: details.account.industry,
      });
    } else {
      this.amplitude.isMasterUser = true;
      this.amplitude.identify(account);
    }
  },

  // Creates an object to send to the integrations
  getCompanyDetails: function () {
    var session = app.session.toJSON(),
      account = session.account,
      details = session.details || {},
      name = "";

    if (details?.account?.name) {
      name = details.account.name;
    }

    return {
      id: account,
      name: name,
      created_at: moment(details.account.created).unix(),
      parent: details.account.parent,
      is_agency: details.account.container,
      industry: details.account.industry,
      monthly_spend: details.plan.subscription === undefined ? 0 : details.plan.subscription.price.totalUSD,
      plan: details.plan.type,
      plan_state: details.plan.state,
      plan_emails: details.plan.emails !== undefined ? details.plan.emails.limit : 0,
      plan_emails_available: details.plan.emails !== undefined ? details.plan.emails.available : 0,
      plan_contacts: details.plan.contacts !== undefined ? details.plan.contacts.limit : 0,
      contacts_active: details.plan.contacts !== undefined ? details.plan.contacts.active : 0,
    };
  },
};
